import React, { useState, useEffect } from 'react';
import { Spin, Button, Select, Space, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import * as dashboardService from '../../services/dashboard/dashboardService';
import MissionControl from './components/MissionControl';
import FulfillmentPipeline from './components/FulfillmentPipeline';
import WorkforcePanel from './components/WorkforcePanel';
import QualityPanel from './components/QualityPanel';
import FinancialTrends from './components/FinancialTrends';

/**
 * Lotus's Same Day Picking Dashboard
 * Route: /dashboard
 */
const Dashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [channelOptions, setChannelOptions] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('all');

  const fetchChannelOptions = async () => {
    try {
      const response = await dashboardService.getChannelOptions();
      if (response?.data) {
        setChannelOptions(response.data.map((opt) => ({ label: opt.label, value: opt.value })));
      }
    } catch (error) {
      console.warn('Failed to load channel options:', error);
    }
  };

  const fetchData = async (channel) => {
    setLoading(true);
    try {
      const response = await dashboardService.getDashboardData(channel);
      if (response.success) {
        setData(response.data);
        setLastRefreshTime(dayjs().format('HH:mm:ss'));
      } else {
        message.error(response.errorMessage || t('dashboard.loadFailed'));
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      message.error(t('dashboard.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChannelOptions();
  }, []);

  useEffect(() => {
    fetchData(selectedChannel);
  }, [selectedChannel]);

  // Auto refresh every 5 minutes
  useEffect(() => {
    const timer = setInterval(() => {
      fetchData(selectedChannel);
    }, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [selectedChannel]);

  return (
    <Spin spinning={loading && !data}>
      <div style={{ padding: '0 0 16px 0', background: '#F0F2F5', minHeight: '100vh' }}>
        {/* Global Filters Bar - sticky top */}
        <div
          style={{
            background: '#fff',
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: 8,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            {/* Left: Title + Filters */}
            <Space size={12}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>{t('dashboard.title')}</span>
              <div>
                <span style={{ fontSize: 12, color: '#999', marginRight: 4 }}>{t('dashboard.channel')}:</span>
                <Select
                  value={selectedChannel}
                  onChange={setSelectedChannel}
                  style={{ width: 160 }}
                  size="small"
                  options={[
                    { label: t('dashboard.allChannels'), value: 'all' },
                    ...channelOptions,
                  ]}
                />
              </div>
            </Space>

            {/* Right: Refresh controls */}
            <Space size={8}>
              {lastRefreshTime && (
                <span style={{ color: '#999', fontSize: 12 }}>
                  {t('dashboard.updated')}: {lastRefreshTime}
                </span>
              )}
              <Button icon={<ReloadOutlined />} size="small" onClick={() => fetchData(selectedChannel)} loading={loading}>
                {t('dashboard.refresh')}
              </Button>
            </Space>
          </div>
        </div>

        {/* Module 1: Mission Control */}
        <div style={{ marginBottom: 16 }}>
          <MissionControl data={data?.missionControl} />
        </div>

        {/* Module 2: Fulfillment Pipeline */}
        <div style={{ marginBottom: 16 }}>
          <FulfillmentPipeline data={data?.pipeline} />
        </div>

        {/* Module 3: Workforce & Productivity */}
        <div style={{ marginBottom: 16 }}>
          <WorkforcePanel data={data?.workforce} />
        </div>

        {/* Module 4: Quality Control & Stock Analysis */}
        <div style={{ marginBottom: 16 }}>
          <QualityPanel data={data?.quality} channel={selectedChannel} />
        </div>

        {/* Module 5: Financial & Trends */}
        <div style={{ marginBottom: 16 }}>
          <FinancialTrends data={data?.financial} />
        </div>
      </div>
    </Spin>
  );
};

export default Dashboard;
